import { V } from "../../app/pages.js";
import { can, emp, leaveScope, me, used } from "../../services/access.js";
import { daysTo, fmtD, today } from "../../domain/dates.js";
import { reportRows } from "../reporting/model.js";
import { inSite, selectedSite, siteCode, siteName } from "../sites/model.js";
import { esc, peso } from "../../domain/format.js";
import { S, ui } from "../../app/store.js";
import { runTot } from "../../domain/payroll.js";
import { LEAVE_CAP } from "../../domain/reference.js";
import { inbox } from "../../services/inbox.js";
import { birCalendar } from "../../domain/compliance.js";
import { NAV } from "../../app/navigation.js";
import { sitePicker } from "../sites/components.js";

V.overview = () => {
  const u = me(),
    financial = can("fs.view"),
    salary = can("hr.salary") || can("pay.approve"),
    from = today().slice(0, 4) + "-01-01",
    rows = financial
      ? reportRows(from, today()).filter(
          (r) => selectedSite() === "ALL" || r.code === selectedSite(),
        )
      : [],
    sum = (k) => rows.reduce((a, r) => a + r[k], 0),
    metrics = [];
  if (financial)
    metrics.push(
      ["Revenue", peso(sum("revenue")), "Posted this year"],
      [
        "Total expenses",
        peso(sum("totalExpenses")),
        "Payroll and operating costs",
      ],
      ["Net result", peso(sum("profit")), "Before income tax"],
      ["Outstanding invoices", peso(sum("outstanding")), "Unpaid as of today"],
    );
  else {
    if (can("hr.view")) {
      const people =
        u.dept && !can("hr.salary")
          ? S.employees.filter((e) => e.dept === u.dept)
          : S.employees;
      metrics.push([
        "Employees",
        String(people.length),
        u.dept && !can("hr.salary") ? u.dept : "Across the company",
      ]);
    }
    if (can("dep.view"))
      metrics.push([
        "Active deployments",
        String(S.deployments.filter((d) => d.status === "ACTIVE").length),
        "Currently assigned to projects",
      ]);
    if (can("pay.view") && salary) {
      const run = [...S.runs].reverse().find((r) => r.status !== "DRAFT");
      if (run)
        metrics.push([
          "Latest net payroll",
          peso(runTot(run).net),
          `${run.id} · ${fmtD(run.pay)}`,
        ]);
    }
    if (can("leave.all") || can("leave.team"))
      metrics.push([
        "Leave requests",
        String(leaveScope().filter((l) => l.status === "PENDING").length),
        "Waiting for a decision",
      ]);
    if (can("ess") && u.emp && metrics.length < 4) {
      const e = emp(u.emp);
      metrics.push([
        "My leave balance",
        `${LEAVE_CAP.SIL - used(e.id, "SIL")} days`,
        "Service incentive leave",
      ]);
    }
    if (can("sys.admin")) {
      metrics.push(
        [
          "Active accounts",
          String(S.users.filter((x) => x.status === "ACTIVE").length),
          "Enabled users",
        ],
        ["Roles", String(Object.keys(S.roles).length), "Access profiles"],
        [
          "Invitations",
          String(S.users.filter((x) => x.status === "INVITED").length),
          "Awaiting acceptance",
        ],
      );
    }
  }
  const all = inbox()
      .map((item, index) => ({ ...item, index }))
      .sort(
        (a, b) =>
          (a.tone === "bad" ? 0 : a.tone === "warn" ? 1 : 2) -
            (b.tone === "bad" ? 0 : b.tone === "warn" ? 1 : 2) ||
          a.index - b.index,
      ),
    kinds = [...new Set(all.map((i) => i.k))],
    filter = kinds.includes(ui.homeKind) ? ui.homeKind : "ALL",
    filtered = all.filter((i) => filter === "ALL" || i.k === filter),
    visible = ui.homeExpanded ? filtered : filtered.slice(0, 5);
  const due = can("bir.view")
      ? birCalendar()
          .filter((b) => b.due && !b.filed)
          .sort((a, b) => a.due.localeCompare(b.due))
          .slice(0, 3)
      : [],
    watch = can("proj.view")
      ? S.projects
          .filter(
            (p) => inSite(p.code) && p.hb && (p.ha / p.hb) * 100 - p.pct > 10,
          )
          .slice(0, 3)
      : [],
    locations = S.sites.filter(
      (s) => selectedSite() === "ALL" || s.id === selectedSite(),
    ),
    canSites = NAV.find((n) => n.id === "sites").ok();
  const tasks = visible
    .map(
      (i) =>
        `<div class="home-task"><span class="home-task-dot ${i.tone === "bad" ? "urgent" : i.tone === "warn" ? "warning" : ""}" aria-hidden="true"></span><div class="home-task-copy"><span class="home-task-kind">${esc(i.k)}</span><strong>${esc(i.t)}</strong><span class="home-task-note" title="${esc(i.s)}">${esc(i.s)}</span></div><div class="home-task-end">${i.amt != null ? `<span class="num">${peso(i.amt)}</span>` : ""}<button class="out" data-go="${esc(i.v)}" aria-label="Review ${esc(i.t)}">Review</button></div></div>`,
    )
    .join("");
  return `<div class="overview-page"><section class="home-snapshot" aria-label="Snapshot"><div class="home-section-head"><div><h2>${financial ? "Financial snapshot" : "At a glance"}</h2><p>${financial ? "Year to date · " + fmtD(today()) : "Your team and current activity"}</p></div>${financial ? sitePicker() : ""}</div>${
    metrics.length
      ? `<div class="home-metrics">${metrics
          .slice(0, 4)
          .map(
            ([label, value, note]) =>
              `<div class="home-metric"><span>${esc(label)}</span><strong>${esc(value)}</strong><small>${esc(note)}</small></div>`,
          )
          .join("")}</div>`
      : '<p class="muted">Use the sidebar to open your workspace.</p>'
  }</section>
 <div class="home-columns"><div class="home-primary"><section class="home-card" aria-label="Tasks requiring attention"><div class="home-section-head"><div><h2>Needs attention <span class="home-count">${all.length}</span></h2><p>Company-wide tasks for your role</p></div>${kinds.length > 1 ? `<select id="home-task-filter" aria-label="Filter tasks"><option value="ALL">All tasks</option>${kinds.map((k) => `<option value="${esc(k)}"${filter === k ? " selected" : ""}>${esc(k)} (${all.filter((i) => i.k === k).length})</option>`).join("")}</select>` : ""}</div>${visible.length ? `<div class="home-task-list">${tasks}</div>${filtered.length > 5 ? `<div class="home-card-footer"><span>Showing ${visible.length} of ${filtered.length}</span><button class="ghost" data-act="home-tasks">${ui.homeExpanded ? "Show less" : "View all tasks"}</button></div>` : ""}` : '<div class="home-empty"><span class="home-clear" aria-hidden="true">✓</span><h3>You’re up to date</h3><p>Tasks that need your attention will appear here.</p></div>'}</section>
 ${watch.length ? `<section class="home-card"><div class="home-section-head"><div><h2>Project watch</h2><p>Projects using hours faster than progress</p></div><button class="ghost" data-go="projects">View projects</button></div>${watch.map((p) => `<div class="home-watch"><div><strong>${esc(p.code)}</strong><span>${esc(siteCode(p))} · ${esc(p.name)}</span></div><span class="home-watch-value">${Math.round((p.ha / p.hb) * 100)}% hours / ${p.pct}% complete</span></div>`).join("")}</section>` : ""}</div>
 <aside class="home-secondary" aria-label="Workspace summary">${
   canSites
     ? `<section class="home-card"><div class="home-section-head"><div><h2>Locations</h2><p>${selectedSite() === "ALL" ? S.sites.length + " sites across the company" : esc(siteName(selectedSite()))}</p></div><button class="ghost" data-go="sites">View all</button></div><div class="home-locations">${
         locations
           .slice(0, 4)
           .map((site) => {
             const ps = S.projects.filter((p) => p.siteId === site.id);
             return `<button data-home-site="${esc(site.id)}"><span class="home-location-icon" aria-hidden="true">⌖</span><span><strong>${esc(site.code)}</strong><small>${esc(site.name)} · ${ps.length} project${ps.length === 1 ? "" : "s"}</small></span><span aria-hidden="true">›</span></button>`;
           })
           .join("") ||
         '<p class="muted">Head office / unassigned is selected.</p>'
       }</div></section>`
     : ""
 }
 ${
   due.length
     ? `<section class="home-card"><div class="home-section-head"><div><h2>Deadlines</h2><p>Next filings to complete</p></div><button class="ghost" data-go="bir">Calendar</button></div>${due
         .map((b) => {
           const days = daysTo(b.due);
           return `<div class="home-deadline"><div><strong>${esc(b.form)}</strong><small>${esc(b.per)}</small></div><div><span class="${days < 0 ? "c-bad" : "muted"}">${days < 0 ? "Overdue" : days === 0 ? "Due today" : fmtD(b.due)}</span>${days < 0 ? `<small>${fmtD(b.due)}</small>` : ""}</div></div>`;
         })
         .join("")}</section>`
     : ""
 }
 ${NAV.find((n) => n.id === "reports").ok() ? `<button class="home-report-link" data-go="reports"><span><strong>Explore your reports</strong><small>Compare sites, projects and periods</small></span><span aria-hidden="true">↗</span></button>` : ""}</aside></div></div>`;
};
